import React from 'react';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';

import {
  ChatFromFacilitator,
  PostChatFromFacilitator
} from '../../services/players';
import {
  FacilitatorChatContext,
  FacilitatorChatContextProps
} from '../../contexts/facilitator-chat';
import { FacilitatorTeamChat } from './chat/team-chat';
import HelpSupport from '../../components/help-support';
import { AllPlayersChat } from './chat/all-players-chat';
import { FacilitatorGroupChat } from './chat/group-chat';
import { sendFacilitatorChat } from '../../services/games';
import { sendFacilitatorTeamChat } from '../../services/teams';
import { GroupResponse } from '../../types/responses/group-response';
import { FacilitatorAndPlayerChat } from './chat/facilitator-player-chat';
import { getGroupList, sendFacilitatorGroupChat } from '../../services/groups';
import { FacilitatorList } from '../../components/facilitator/facilitator-list';
import { GameContext } from '../../contexts/game';

export const ChatPage = () => {
  const { gameCode } = useParams();
  const { setNewChat } = React.useContext(
    FacilitatorChatContext
  ) as FacilitatorChatContextProps;
  const [game] = React.useContext(GameContext);

  const [selectedItem, setSelectedItem] = React.useState<
    | {
        id: string;
        name: string;
        type: 'global-chat' | 'player-chat' | 'team-chat' | 'group-chat';
      }
    | undefined
  >();
  const [groups, setGroups] = React.useState<GroupResponse[]>([]);

  const players = game.gameState?.players ?? [];
  const teams = game.gameState?.teams ?? [];

  // const [playersChatHistory, setPlayersChatHistory] = useState<
  //   PlayerContactResponse[]
  // >([]);

  // const getPlayersChatHistory = useCallback(async () => {
  //   let result = (await getPlayersChatHistoryAsync(gameCode!)) as any;
  //   setPlayersChatHistory(result as PlayerContactResponse[]);
  // }, [gameCode]);

  const auth = useAuth();
  const userId = parseInt(auth.user?.profile.sub!);
  const userName = auth.user?.profile.name!;

  const onSendMessageHandler = React.useCallback(
    async (message: string) => {
      if (selectedItem?.type === 'global-chat') {
        await sendFacilitatorChat(gameCode, userId!, userName!, message!);
        setNewChat((prev) => prev.filter((c) => c.group !== 'global-chat'));
      }

      if (selectedItem?.type === 'group-chat') {
        await sendFacilitatorGroupChat(
          gameCode,
          selectedItem.id,
          userId!,
          userName!,
          message!
        );
        setNewChat((prev) =>
          prev.filter(
            (c) =>
              !(
                c.group === 'group-chat' && c.author.groupId === selectedItem.id
              )
          )
        );
      }

      if (selectedItem?.type === 'team-chat') {
        await sendFacilitatorTeamChat(
          gameCode,
          selectedItem.id,
          userId!,
          userName!,
          message!
        );
        setNewChat((prev) =>
          prev.filter(
            (c) =>
              !(c.group === 'team-chat' && c.author.teamId === selectedItem.id)
          )
        );
      }

      if (selectedItem?.type === 'player-chat') {
        const payload: ChatFromFacilitator = {
          facilitatorName: userName,
          facilitatorUserProfileId: userId,
          toPlayerCode: selectedItem?.id ?? '',
          message: message
        };

        await PostChatFromFacilitator(gameCode!, payload);
        setNewChat((prev) =>
          prev.filter(
            (c) =>
              !(c.group === 'player-chat' && c.author.id === selectedItem.id)
          )
        );
      }
    },
    [userId, gameCode, userName, selectedItem]
  );

  React.useEffect(() => {
    if (!gameCode) return;
    // getPlayersChatHistory();
    (async () => {
      const groups = await getGroupList(gameCode);
      setGroups(groups as GroupResponse[]);
    })();
  }, [gameCode]);

  return (
    <>
      <div className="d-flex flex-column flex-grow-1 h-100 w-100 overflow-hidden">
        <HelpSupport
          title="Chat guide"
          url="https://forum.catalystglobal.com/t/4650"
        />
        <div className="row flex-grow-1 h-100 m-0 p-0 overflow-hidden">
          <div className="col-3 h-100 overflow-auto d-flex flex-column gap-2">
            <FacilitatorList
              listData={[{ id: 0, code: gameCode, name: 'All players' }]}
              selectedItem={selectedItem as any}
              setSelectedItem={setSelectedItem}
              listType="global-chat"
            />

            {groups.length > 0 && (
              <div className={'d-flex flex-column'}>
                <small className={'fw-bold sticky-top bg-white pb-1'}>
                  Group chat
                </small>
                <FacilitatorList
                  listData={groups as any}
                  selectedItem={selectedItem as any}
                  setSelectedItem={setSelectedItem}
                  listType={'group-chat'}
                />
              </div>
            )}

            {teams.length > 0 && (
              <div className={'d-flex flex-column'}>
                <small className={'fw-bold sticky-top bg-white pb-1'}>
                  Team chat
                </small>

                <FacilitatorList
                  listData={teams as any}
                  selectedItem={selectedItem as any}
                  setSelectedItem={setSelectedItem}
                  listType={'team-chat'}
                />
              </div>
            )}

            {players.length > 0 && (
              <div className={'d-flex flex-column'}>
                <small className={'fw-bold sticky-top bg-white pb-1'}>
                  Player chat
                </small>

                <FacilitatorList
                  listData={players as any}
                  selectedItem={selectedItem as any}
                  setSelectedItem={setSelectedItem}
                  listType={'player-chat'}
                />
              </div>
            )}

            {/* <FacilitatorList
              listData={playersChatHistory as any}
              onClick={onClickPlayerChatHitoryHandler}
              dataItem={selectedItem as any}
            /> */}
          </div>

          <div className="col-9">
            {selectedItem?.type === 'global-chat' && (
              <AllPlayersChat
                item={selectedItem}
                onSendMessage={onSendMessageHandler}
              />
            )}

            {selectedItem?.type === 'group-chat' && (
              <FacilitatorGroupChat
                key={selectedItem.id}
                item={selectedItem}
                onSendMessage={onSendMessageHandler}
              />
            )}

            {selectedItem?.type === 'team-chat' && (
              <FacilitatorTeamChat
                key={selectedItem.id}
                item={selectedItem}
                onSendMessage={onSendMessageHandler}
              />
            )}

            {selectedItem?.type === 'player-chat' && (
              <FacilitatorAndPlayerChat
                key={selectedItem.id}
                item={selectedItem}
                onSendMessage={onSendMessageHandler}
              />
            )}

            {/* {(selectedTeamItem as PlayerContactResponse).playerCode1 &&
              chatType === 'PlayerHistory' && (
                <FacilitatorBetweenPlayerChat
                  item={selectedItem as any}
                  onSendMessage={onSendMessageHandler}
                />
              )} */}
          </div>
        </div>
      </div>
    </>
  );
};
